import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getConversationMessages, apiPost, markConversationUnread, deleteMessage, searchUsers, createNewMessage, editMessage, addReaction, getReactions } from '../../utils/api';
import ChatMessageInput from '../chat/ChatMessageInput';
import EmojiPicker from 'emoji-picker-react';

const ChatPanel = ({
    activeConversation,
    activeConv,
    onMessageSent,
    isNewMessageMode = false,
    onNewMessageCancel,
    onNewMessageSent,
    onBack
}) => {

    // Get user data from Redux
    const user = useSelector((state) => state.auth.user);
    const jwtToken = useSelector((state) => state.auth.jwtToken);
    const socketConnection = useSelector((state) => state.common?.socket);
    
    // Refs
    const messagesEndRef = useRef(null);
    const chatMessagesRef = useRef(null);
    
    // Local state for messages
    const [messages, setMessages] = useState([]);
    const [messagesPagination, setMessagesPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
    });
    
    // Thread/Group info
    const [threadInfo, setThreadInfo] = useState(null);
    const [recipientsData, setRecipientsData] = useState([]);
    const [membersData, setMembersData] = useState([]);
    
    // Local state for input
    const [messageInput, setMessageInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [error, setError] = useState(null);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    
    // Typing indicators
    const [typingUsers, setTypingUsers] = useState(new Map());
    const typingTimeoutRef = useRef({});
    
    // Message status
    const [messageStatus, setMessageStatus] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    
    // Reply to message state
    const [replyingToMessage, setReplyingToMessage] = useState(null);
    
    // Context menu state
    const [showHeaderMenu, setShowHeaderMenu] = useState(false);
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [messageContextMenu, setMessageContextMenu] = useState(null); // { messageId, x, y }
    const headerMenuRef = useRef(null);
    const messageMenuRef = useRef(null);
    const reactionPickerRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const touchStartRef = useRef(null);
    
    // Delete messages mode
    const [deleteMode, setDeleteMode] = useState(false);
    const [selectedMessages, setSelectedMessages] = useState(new Set());

    // Reactions
    const [reactionPicker, setReactionPicker] = useState(null); // { messageId }
    // Mirror mobile emoji set
    const quickReactions = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👏', '🔥', '🎉', '💯'];
    
    // New message mode state
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [contacts, setContacts] = useState([]);
    const [recipientSearchResults, setRecipientSearchResults] = useState([]);
    const [showRecipientSearch, setShowRecipientSearch] = useState(false);
    const [showContacts, setShowContacts] = useState(true);
    const [isSearchingRecipients, setIsSearchingRecipients] = useState(false);
    const recipientSearchRef = useRef(null);
    const recipientInputRef = useRef(null);
    
    // ====== UTILITY FUNCTIONS ======
    const scrollToBottom = () => {
        if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    };

    const scrollToMessage = (messageId) => {
        if (!messageId || !chatMessagesRef.current) return;

        const container = chatMessagesRef.current;
        const target = container.querySelector(`[data-message-id="${messageId}"]`);

        if (!target) {
            // Message might be in older page; currently only scroll if already loaded
            return;
        }

        // Scroll smoothly so the message is nicely in view
        target.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });

        // Add a temporary highlight class for context
        target.classList.add('octopus-message-highlight');
        setTimeout(() => {
            target.classList.remove('octopus-message-highlight');
        }, 2500);
    };

    // Ensure a message exists in the loaded pages; if not, load older pages until found, then scroll
    const ensureMessageVisibleAndScroll = async (messageId, maxPagesToLoad = 10) => {
        if (!messageId) return;

        // If already in current messages, just scroll
        const alreadyLoaded = messages.find((m) => m.id === messageId);
        if (alreadyLoaded) {
            scrollToMessage(messageId);
            return;
        }

        // Load older pages until we either find the message or run out of pages
        let currentPage = messagesPagination.page || 1;
        const totalPages = messagesPagination.total_pages || 1;
        let pagesLoaded = 0;

        while (pagesLoaded < maxPagesToLoad && currentPage < totalPages) {
            const nextPage = currentPage + 1;

            // Fetch older messages (page 2, 3, ...)
            const fetchedMessages = await fetchMessagesData(nextPage);

            pagesLoaded += 1;
            currentPage = nextPage;

            // If API didn't return anything, stop
            if (!Array.isArray(fetchedMessages) || fetchedMessages.length === 0) {
                break;
            }

            const foundInBatch = fetchedMessages.find((m) => m.id === messageId);
            if (foundInBatch) {
                // Give React a moment to render, then scroll
                setTimeout(() => {
                    scrollToMessage(messageId);
                }, 200);
                return;
            }
        }
    };
    
    const formatMessageTime = (date) => {
        const messageDate = new Date(date);
        return messageDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };
    
    const formatMessageDate = (date) => {
        const messageDate = new Date(date);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        if (messageDate.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (messageDate.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return messageDate.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        }
    };
    
    const isMyMessage = (message) => {
        return message.author?.id === user?.id || 
               message.sender_id === user?.id ||
               message.userData?.id === user?.id;
    };
    
    const decodeHtmlEntities = (html) => {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
    };

    const getPlainMessageText = (html = '') => {
        const noTags = (html || '').replace(/<[^>]+>/g, '');
        return decodeHtmlEntities(noTags);
    };
    
    const sanitizeMessageHtml = (html) => {
        if (!html) return '';
        

        const decoded = decodeHtmlEntities(html);
        const converted = decoded.replace(/\n/g, '<br />');

        const normalized = converted.replaceAll(/\\'/g, "'");


        if (typeof window === 'undefined' || !window.DOMParser) {
            return normalized;
        }

        const allowedTags = new Set([
            'a',
            'b',
            'strong',
            'i',
            'em',
            'u',
            'br',
            'p',
            'div',
            'span',
            'ul',
            'ol',
            'li',
            'blockquote',
            'code',
            'pre',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'small',
            'strong',
            'u',
            'del',
            'ins',
            's',
            'sub',
            'sup',
            'table',
            'tbody',
            'thead',
            'tfoot',
            'tr',
            'td',
            'th',
            'textarea',
            'input',
            'button',
            'select',
            'option',
            'optgroup',
            'legend',
            'fieldset',
            'label',
            'form',
            'link',
            'img',
            'abbr',
            'acronym',
            'area',
            'audio',
            'video',
            'source',
            'track',
            'map',
            'iframe',
            'embed',
            'object',
            'param',
            'picture',
            'source',
            'track',
            'map',
            'iframe',
            'embed',
            'object',
            'param',
            'picture',
        ]);

        const allowedAttributes = {
            a: ['href', 'title', 'target', 'rel', 'class', 'id', 'style', 'data-*', 'role', 'aria-controls', 'aria-selected', 'disabled'],
            div: ['style', 'class', 'id', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            li: ['style', 'class', 'id', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            span: ['style', 'class', 'id', 'data-*', 'aria-hidden'],
            style: ['class', 'id', 'type'],
            fieldset: ['class', 'id', 'type'],
            link: ['class', 'id', 'type', 'rel', 'href', 'media'],
            form: ['style', 'class', 'id', 'method', 'action', 'data-*'],
            img: ['src', 'alt', 'title', 'width', 'height', 'class', 'data-*'],
            h1: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            h2: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            h3: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            h4: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            h5: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            h6: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            ul: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            ol: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            li: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            p: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            br: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            hr: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            blockquote: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            code: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            pre: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            em: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            strong: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            u: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            del: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            ins: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            s: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            small: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            sub: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            sup: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            table: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            tbody: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            thead: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            tfoot: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            tr: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            td: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            th: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            textarea: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            input: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            button: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            select: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            option: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            optgroup: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            legend: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            fieldset: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
            label: ['class', 'id', 'style', 'data-*', 'role', 'aria-labelledby', 'value', 'aria-modal', 'tabindex'],
        };

        const parser = new DOMParser();
        const doc = parser.parseFromString(normalized, 'text/html');

        const sanitizeNode = (node) => {
            if (!node) return;

            // Text node
            if (node.nodeType === Node.TEXT_NODE) {
                return;
            }

            // Element node
            if (node.nodeType === Node.ELEMENT_NODE) {
                const tagName = node.nodeName.toLowerCase();

                if (!allowedTags.has(tagName)) {
                    const parent = node.parentNode;
                    if (parent) {
                        while (node.firstChild) {
                            parent.insertBefore(node.firstChild, node);
                        }
                        parent.removeChild(node);
                    }
                    return;
                }

                Array.from(node.attributes).forEach((attr) => {
                    const attrName = attr.name.toLowerCase();
                    const allowedForTag = allowedAttributes[tagName] || [];
                    const isAllowed = allowedForTag.includes(attrName);

                    if (!isAllowed) {
                        node.removeAttribute(attr.name);
                        return;
                    }

                    if (tagName === 'a' && attrName === 'href') {
                        const hrefValue = node.getAttribute('href') || '';
                        if (/^javascript:/i.test(hrefValue.trim())) {
                            node.removeAttribute('href');
                        }
                    }
                });

                if (tagName === 'a' && node.getAttribute('target') === '_blank') {
                    node.setAttribute('rel', 'noopener noreferrer');
                }
            }

            Array.from(node.childNodes).forEach((child) => sanitizeNode(child));
        };

        Array.from(doc.body.childNodes).forEach((child) => sanitizeNode(child));

        return doc.body.innerHTML;
    };

    const isSafeUrl = (url) => {
        if (!url) return false;

        try {
            const parsed = new URL(url, window.location.origin);
            return parsed.protocol === 'https:' || parsed.protocol === 'http:';
        } catch (err) {
            return false;
        }
    };

    const getAttachmentPreviewUrl = (attachment = {}) => {
        const candidates = [
            attachment.finalPath,
            attachment.guid,
            attachment.url,
            attachment.media?.url,
        ];

        return candidates.find((candidate) => isSafeUrl(candidate)) || null;
    };

    const getAttachmentDisplayName = (attachment = {}) => {
        return (
            attachment.post_title ||
            attachment.title ||
            attachment.filename ||
            attachment.post_name ||
            attachment.media?.title ||
            'Attachment'
        );
    };

    const getAttachmentKind = (attachment = {}) => {
        const mediaType = (attachment.media?.type || attachment.type || '').toLowerCase();
        const mimeType = (attachment.post_mime_type || attachment.mime_type || '').toLowerCase();

        if (mediaType.includes('photo') || mediaType === 'image') {
            return 'photo';
        }

        if (mediaType.includes('video')) {
            return 'video';
        }

        if (mimeType.startsWith('image/')) {
            return 'photo';
        }

        if (mimeType.startsWith('video/')) {
            return 'video';
        }

        return 'file';
    };

    const getAttachmentDownloadUrl = (attachment = {}) => {
        const candidates = [
            attachment.downloadUrl,
            attachment.download_url,
            attachment.finalPath,
            attachment.guid,
            attachment.url,
            attachment.media?.downloadUrl,
            attachment.media?.url,
        ];

        return candidates.find((candidate) => isSafeUrl(candidate)) || null;
    };

    const normalizeReactions = (reactionsData = []) => {
        // If backend returns strings like "userId___emoji"
        if (Array.isArray(reactionsData) && reactionsData.every((item) => typeof item === 'string')) {
            const buckets = reactionsData.reduce((acc, reactionStr) => {
                const [userId, emojiValue] = reactionStr.split('___');
                if (!emojiValue) return acc;
                const emoji = decodeHtmlEntities(emojiValue);
                const id = parseInt(userId, 10);
                if (!acc[emoji]) {
                    acc[emoji] = [];
                }
                acc[emoji].push(id);
                return acc;
            }, {});

            return Object.entries(buckets).map(([emoji, users]) => ({
                emoji,
                users,
            }));
        }

        // Already in object format { emoji, users: [...] }
        return reactionsData;
    };

    const normalizeMessageReactions = (message = {}) => {
        // Backend might return reactions or emojis; normalize both
        const raw = message.reactions || message.emojis || [];
        return {
            ...message,
            reactions: normalizeReactions(raw),
        };
    };

    const refreshMessageReactions = async (messageId) => {
        if (!messageId) return;
        try {
            const res = await getReactions(messageId);
            const data = res?.data?.getReactionsData || res?.data || [];
            const normalized = normalizeReactions(data);
            setMessages((prev) =>
                prev.map((m) => (m.id === messageId ? { ...m, reactions: normalized } : m))
            );
        } catch (err) {
            console.error('Failed to refresh reactions', err);
        }
    };

    // Reactions helper: toggle add/remove for a message
    const updateMessageReactions = ({ messageId, emoji, userId, remove = false }) => {
        if (!messageId || !emoji || !userId) return;

        setMessages((prev) =>
            prev.map((message) => {
                if (message.id !== messageId) return message;

                const normalizedReactions = Array.isArray(message.reactions)
                    ? message.reactions.map((r) => ({
                          emoji: r.emoji || r.reaction || '',
                          users: Array.isArray(r.users) ? r.users.map((u) => (typeof u === 'object' ? u.id || u.user_id || u : u)) : [],
                      }))
                    : [];

                const userIdStr = String(userId);
                const reactions = [...normalizedReactions];
                const existingIndex = reactions.findIndex((r) => r.emoji === emoji);

                if (existingIndex !== -1) {
                    const existing = reactions[existingIndex];
                    const alreadyReacted = existing.users.some((id) => String(id) === userIdStr);
                    const remainingUsers = existing.users.filter((id) => String(id) !== userIdStr);

                    if (remove || alreadyReacted) {
                        if (remainingUsers.length === 0) {
                            reactions.splice(existingIndex, 1);
                        } else {
                            reactions[existingIndex] = { ...existing, users: remainingUsers };
                        }
                    } else {
                        reactions[existingIndex] = { ...existing, users: [...existing.users, userId] };
                    }
                } else if (!remove) {
                    reactions.push({ emoji, users: [userId] });
                }

                return { ...message, reactions };
            })
        );
    };

    const handleAttachmentPreview = (attachment, url) => {
        if (!isSafeUrl(url)) {
            return;
        }

        setPreviewAttachment({
            url,
            name: getAttachmentDisplayName(attachment),
        });
    };

    const handleClosePreview = () => {
        setPreviewAttachment(null);
    };
    
    // Fetch messages for active conversation
    // PAGINATION LOGIC:
    // - Page 1 = newest messages (displayed at bottom)
    // - Page 2+ = older messages (displayed at top when loaded)
    // - Scroll UP to load older messages (increment page)
    // - Messages sorted oldest → newest (bottom = latest)
    const fetchMessagesData = async (pageNumber = null, preserveScrollMessageId = null, scrollHeightBefore = 0) => {
        if (!jwtToken || !activeConversation) return [];
        
        const currentPage = pageNumber || messagesPagination.page;
        
        // Set appropriate loading state
        if (currentPage === 1) {
            setIsLoading(true);
        } else {
            setIsLoadingMore(true);
        }
        
        setError(null);
        
        try {
            const response = await getConversationMessages(activeConversation, {
                limit: messagesPagination.limit,
                page: currentPage,
            });

            
            
            // Handle response structure: data.messagesData.messages
            let fetchedMessages = [];
            let threadInfo = null;
            let totalPages = 0;
            
            if (response.data?.messagesData) {
                // New API structure
                fetchedMessages = response.data.messagesData.messages || [];
                threadInfo = response.data.messagesData.group || null;
                totalPages = response.data.messagesData.total_page || 0;

                // Store recipients data (filter out current user)
                if (response.data.messagesData.recipients && response.data.messagesData.recipients.length > 0) {
                    const filteredRecipients = response.data.messagesData.recipients.filter(
                        recipient => recipient.user_id !== user?.id
                    );
                    setRecipientsData(filteredRecipients);
                    setMembersData(response.data.messagesData.recipients);
                    

                    console.log("filteredRecipients", filteredRecipients);
                }
                
                // Store thread info if available
                if (threadInfo) {
                    setThreadInfo(threadInfo);
                }
            } else if (response.data) {
                // Fallback: direct data structure
                fetchedMessages = Array.isArray(response.data) ? response.data : response.data.messages || [];
            } else if (Array.isArray(response)) {
                // Fallback: array response
                fetchedMessages = response;
            }


            console.log(fetchedMessages);

            
            // Update messages
            setMessages(prevMessages => {
                if (currentPage === 1) {
                    // Page 1 = newest messages, replace everything
                    // Sort to ensure latest messages are at bottom
                    return fetchedMessages
                        .map(normalizeMessageReactions)
                        .sort(
                        (a, b) => new Date(a.date || a.date_sent) - new Date(b.date || b.date_sent)
                    );
                }
                
                // For pagination:
                // - Page 2+ = older messages (should be PREPENDED to array, not appended)
                // - This keeps newest messages at the bottom
                const allMessages = [...fetchedMessages.map(normalizeMessageReactions), ...prevMessages]; // Older messages first
                const uniqueMessages = Array.from(
                    new Map(allMessages.map(message => [message.id, message])).values()
                );
                
                // Sort: oldest → newest (ascending order)
                // This means newest messages appear at the BOTTOM of the chat
                return uniqueMessages.sort(
                    (a, b) => new Date(a.date || a.date_sent) - new Date(b.date || b.date_sent)
                );
            });
            
            // Restore scroll position after loading older messages
            if (currentPage > 1 && preserveScrollMessageId && scrollHeightBefore > 0) {
                setTimeout(() => {
                    const scrollContainer = chatMessagesRef.current;
                    if (scrollContainer) {
                        const scrollHeightAfter = scrollContainer.scrollHeight;
                        const heightDifference = scrollHeightAfter - scrollHeightBefore;
                        // Maintain scroll position by adding the height difference
                        scrollContainer.scrollTop = scrollContainer.scrollTop + heightDifference;
                    }
                }, 100);
            }
            
            // Update pagination
            // Note: For chat, page 1 = newest messages, higher pages = older messages
            // When scrolling UP (to see older), we load NEXT page (page 2, 3, etc.)
            if (response.pagination) {
                // If API provides pagination object
                setMessagesPagination(response.pagination);
            } else if (totalPages > 0) {
                // Calculate pagination from total_page
                setMessagesPagination(prev => ({
                    ...prev,
                    page: currentPage,
                    total: totalPages * prev.limit, // Approximate total
                    total_pages: totalPages,
                    // has_next means "has older messages" (scroll up to load)
                    has_next: currentPage < totalPages,
                    // has_prev means "has newer messages" (scroll down to load) - usually none on page 1
                    has_prev: currentPage > 1,
                }));
            } else {
                // Fallback pagination calculation
                setMessagesPagination(prev => ({
                    ...prev,
                    page: currentPage,
                    total: fetchedMessages.length,
                    total_pages: Math.ceil(fetchedMessages.length / prev.limit),
                    has_next: fetchedMessages.length === prev.limit,
                    has_prev: currentPage > 1,
                }));
            }
            
            // Scroll to bottom on initial load (page 1)
            if (currentPage === 1 && fetchedMessages.length > 0) {
                setTimeout(() => scrollToBottom(), 100);
            }

            // Return fetched messages so callers (like ensureMessageVisibleAndScroll) can inspect them
            return fetchedMessages;
        } catch (err) {
            console.error('Failed to fetch messages:', err);
            setError(err.message || 'Failed to load messages');
            return [];
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
            setIsPageLoading(false);
        }
    };
    
    
    // Handle send message
    const handleSendMessage = async (payload = {}) => {
        if (!activeConversation || !jwtToken) return false;

        const textFromPayload =
            typeof payload.message === 'string'
                ? payload.message
                : typeof payload.text === 'string'
                ? payload.text
                : messageInput;
        const messageToSend = (textFromPayload || '').trim();

        const attachmentsArray = Array.isArray(payload.attachments)
            ? payload.attachments
            : [];

        const images =
            Array.isArray(payload.images) && payload.images.length > 0
                ? payload.images
                : attachmentsArray.filter(({ kind }) => kind === 'image');

        const videos =
            Array.isArray(payload.videos) && payload.videos.length > 0
                ? payload.videos
                : attachmentsArray.filter(({ kind }) => kind === 'video');

        const documents =
            Array.isArray(payload.documents) && payload.documents.length > 0
                ? payload.documents
                : attachmentsArray.filter(
                      ({ kind }) => kind === 'document' || (kind !== 'image' && kind !== 'video')
                  );

        if (!messageToSend && images.length === 0 && videos.length === 0 && documents.length === 0) {
            return false;
        }
        
        setSendingMessage(true);
        setError(null);
        
        try {
            const formData = new FormData();
            formData.append('message', messageToSend);
            
            // Add reply_to if replying to a message
            if (replyingToMessage && replyingToMessage.id) {
                formData.append('reply_to', replyingToMessage.id);
            }

            const appendFiles = (items = [], fieldName) => {
                items.forEach((item, index) => {
                    if (!item) return;
                    if (item.file instanceof File || item.file instanceof Blob) {
                        formData.append(
                            fieldName,
                            item.file,
                            item.file.name || `${fieldName}-${Date.now()}-${index}`
                        );
                    } else if (item instanceof File || item instanceof Blob) {
                        formData.append(fieldName, item, item.name || `${fieldName}-${Date.now()}-${index}`);
                    }
                });
            };

            appendFiles(images, 'images');
            appendFiles(videos, 'videos');
            appendFiles(documents, 'documents');

            const apiRoot =
                (typeof window !== 'undefined' && window.octopusReactData?.apiUrl?.replace(/\/$/, '')) ||
                (typeof process !== 'undefined' && process.env.NODE_BACKEND) ||
                'https://octopusworkapp.com:9001';

            const url = `/messages/sendMessage/?threadId=${activeConversation}`;
            const response = await fetch(`${apiRoot}${url}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${jwtToken}`,
                },
                body: formData,
            });

            const responseData = await response.json().catch(() => ({}));

            if (!response.ok || !responseData?.isSuccess) {
                throw new Error(responseData?.message || 'Failed to send message');
            }

            const optimisticMessage = {
                id: responseData?.data?.message_id || Date.now(),
                content: messageToSend,
                message: messageToSend,
                type: 'text',
                author: {
                    id: user?.id,
                    name: user?.display_name || 'You',
                    email: user?.email,
                },
                userData: {
                    id: user?.id,
                    display_name: user?.display_name || 'You',
                    image: user?.avatar_urls?.full || user?.image,
                },
                sender_id: user?.id,
                date: new Date().toISOString(),
                date_sent: new Date().toISOString(),
                timestamp: Date.now(),
            };

            setMessages((prev) => [...prev, optimisticMessage]);
            if (onMessageSent) {
                onMessageSent(optimisticMessage);
            }

            // Emit socket events for real-time updates
            if (socketConnection && responseData?.data?.message_id) {
                const recipientIds = recipientsData.map(recipient => recipient.user_id);
                const fcmTokenUsers = recipientIds;
                recipientIds.push(user?.id);

                const dataMessages = {
                    recipientIds,
                    messageId: responseData.data.message_id
                };
                
                socketConnection.emit('sendmessage', dataMessages);
                socketConnection.emit('fcmTokens', {
                    fcmTokenUsers,
                    messageId: responseData.data.message_id
                });

                // Emit seenUser for recipients
                if (recipientIds.length > 0) {
                    socketConnection.emit('seenUser', {
                        userIds: recipientsData.map(r => r.user_id),
                        messageId: responseData.data.message_id,
                        userId: user?.id
                    });
                }
            }

            setMessageInput('');
            setReplyingToMessage(null); // Clear reply after sending
            setTimeout(() => scrollToBottom(), 100);

            return true;
        } catch (err) {
            console.error('Failed to send message:', err);
            setError(err.message || 'Failed to send message');
            return false;
        } finally {
            setSendingMessage(false);
        }
    };
    
    // Handle edit message
    const handleEditExistingMessage = async (payload = {}) => {
        if (!activeConversation || !jwtToken || !editingMessage) return false;

        const textFromPayload =
            typeof payload.message === 'string'
                ? payload.message
                : typeof payload.text === 'string'
                ? payload.text
                : messageInput;
        const updatedMessage = (textFromPayload || '').trim();

        if (!updatedMessage) {
            return false;
        }

        setSendingMessage(true);
        setError(null);

        try {
            await editMessage(editingMessage.id, { message: updatedMessage });

            // Update local state optimistically
            setMessages((prev) =>
                prev.map((m) =>
                    m.id === editingMessage.id
                        ? {
                              ...m,
                              message: updatedMessage,
                              content: updatedMessage,
                              updated_at: new Date().toISOString(),
                          }
                        : m
                )
            );

            // Emit socket event for real-time updates
            if (socketConnection && recipientsData.length > 0) {
                const recipientIds = recipientsData.map((recipient) => recipient.user_id);
                recipientIds.push(user?.id);

                socketConnection.emit('editmessage', {
                    recipientIds,
                    messageId: editingMessage.id,
                    message: updatedMessage,
                    threadId: activeConversation,
                });
            }

            setEditingMessage(null);
            setMessageInput('');
            return true;
        } catch (err) {
            console.error('Failed to edit message:', err);
            setError(err.message || 'Failed to edit message');
            return false;
        } finally {
            setSendingMessage(false);
        }
    };
    
    // ====== EFFECTS ======
    
    // Socket connection listener - comprehensive WebSocket handling
    useEffect(() => {
        if (socketConnection && activeConversation) {
            const handleResponse = (data) => {
                console.log('Received from server:', data);
            };

            const handleSeenUser = async (data) => {
                if (data && data.id && data.thread_id && data.thread_id === activeConversation) {
                    let seenUserss = null;
                    if (data.seenUsers && data.seenUsers.length > 0) {
                        seenUserss = data.seenUsers.filter(userId => parseInt(userId) !== parseInt(user?.id));
                        if (seenUserss && seenUserss.length > 0) {
                            setMessageStatus("Seen");
                        }
                    }
                }
            };

            const handleSendReaction = async (data) => {
                const messageId = data?.messageId || data?.message_id;
                if (messageId) {
                    refreshMessageReactions(messageId);
                }
            };

            const handleSendMessageRes = (data) => {
                console.log('Send message response received:', data);
                if (data && data.message && data.message.id) {
                    if (data.message.thread_id == activeConversation) {
                        // Clear typing indicator for sender
                        if (data.message.sender_id && typingTimeoutRef.current[data.message.sender_id]) {
                            clearTimeout(typingTimeoutRef.current[data.message.sender_id]);
                            setTypingUsers(prev => {
                                const newMap = new Map(prev);
                                newMap.delete(data.message.sender_id);
                                return newMap;
                            });
                        }

                        // Add message directly to messages array (real-time update)
                        setMessages(prevMessages => {
                            // Check if message already exists
                            const exists = prevMessages.find(m => m.id === data.message.id);
                            if (exists) {
                                // If message already exists, replace with new message (show new message)
                                return prevMessages.map(m => m.id === data.message.id ? data.message : m);
                            }
                            return [...prevMessages, data.message];
                        });

                        // Scroll to bottom
                        setTimeout(() => scrollToBottom(), 100);

                        // Emit seenUser if there are recipients
                        if (data.recipientIds && data.recipientIds.length > 0 && socketConnection) {
                            socketConnection.emit('seenUser', {
                                userIds: data.recipientIds,
                                messageId: data.message.id,
                                userId: user?.id
                            });
                        }
                    }
                } else {
                    // If message structure is different, refetch
                    fetchMessagesData(1);
                }
            };

            const handleEditMessageRes = (data) => {
                if (data && data.message && data.message.id) {
                    if (data.message.thread_id == activeConversation) {
                        // Update message in real-time
                        setMessages(prev => prev.map(m => 
                            m.id === data.message.id 
                                ? { ...m, message: data.message.message } 
                                : m
                        ));
                    } else {
                        fetchMessagesData(1);
                    }
                }
            };

            const handleDeleteMessageRes = (data) => {
                if (data && data.messageIds && data.messageIds.length > 0) {
                    // Update messages to mark as deleted
                    setMessages(prev => prev.map(m => 
                        data.messageIds.includes(m.id) 
                            ? { ...m, is_deleted: true } 
                            : m
                    ));
                }
            };

            const handleTypingStart = (data) => {
                // If the typing event is from the current user, do nothing
                if (user?.id === data.userId) {
                    return false;
                }
                if (data.threadId === activeConversation) {
                    setTypingUsers(prev => {
                        const newMap = new Map(prev);
                        newMap.set(data.userId, data.typingUser);
                        return newMap;
                    });

                    // Clear existing timeout for this user
                    if (typingTimeoutRef.current[data.userId]) {
                        clearTimeout(typingTimeoutRef.current[data.userId]);
                    }

                    // Set new timeout to remove typing indicator after 3 seconds
                    typingTimeoutRef.current[data.userId] = setTimeout(() => {
                        setTypingUsers(prev => {
                            const newMap = new Map(prev);
                            newMap.delete(data.userId);
                            return newMap;
                        });
                    }, 3000);
                }
            };

            // Register all socket listeners
            socketConnection.on('response', handleResponse);
            socketConnection.on('sendMessageRes', handleSendMessageRes);
            socketConnection.on('editMessageRes', handleEditMessageRes);
            socketConnection.on('deleteMessageRes', handleDeleteMessageRes);
            socketConnection.on('typingStart', handleTypingStart);
            socketConnection.on('seenUser', handleSeenUser);
            socketConnection.on('sendReaction', handleSendReaction);
            
            return () => {
                // Cleanup: remove all listeners
                socketConnection.off('response', handleResponse);
                socketConnection.off('sendMessageRes', handleSendMessageRes);
                socketConnection.off('editMessageRes', handleEditMessageRes);
                socketConnection.off('deleteMessageRes', handleDeleteMessageRes);
                socketConnection.off('typingStart', handleTypingStart);
                socketConnection.off('seenUser', handleSeenUser);
                socketConnection.off('sendReaction', handleSendReaction);
                
                // Clear all typing timeouts
                Object.values(typingTimeoutRef.current).forEach(timeout => clearTimeout(timeout));
            };
        }
    }, [socketConnection, activeConversation, user?.id]);
    
    // Handle seen status for last message
    useEffect(() => {
        if (messages.length > 0 && socketConnection && recipientsData.length > 0) {
            const lastMessage = messages[messages.length - 1];
            
            // Only mark as seen if it's not our own message
            const isNotMyMessage = lastMessage && (
                lastMessage.author?.id !== user?.id &&
                lastMessage.sender_id !== user?.id &&
                lastMessage.userData?.id !== user?.id
            );
            
            if (lastMessage && lastMessage.id && isNotMyMessage) {
                // Emit seenUser for the last message
                const userIds = recipientsData.map(item => item.user_id);
                socketConnection.emit('seenUser', {
                    userIds,
                    messageId: lastMessage.id,
                    userId: user?.id
                });
            }
        }
    }, [messages, socketConnection, recipientsData, user?.id]);

    // Fetch messages when active conversation changes
    useEffect(() => {
        if (activeConversation && user?.id) {
            setMessages([]);
            setThreadInfo(null);
            setRecipientsData([]);
            setTypingUsers(new Map());
            setMessageStatus(null);
            setEditingMessage(null);
            setReplyingToMessage(null);
            setMessageInput('');
            setMessagesPagination(prev => ({ ...prev, page: 1 }));
            fetchMessagesData(1);
        } else {
            setMessages([]);
            setThreadInfo(null);
            setRecipientsData([]);
            setTypingUsers(new Map());
            setMessageStatus(null);
            setEditingMessage(null);
            setReplyingToMessage(null);
            setMessageInput('');
            setMessagesPagination({
                page: 1,
                limit: 50,
                total: 0,
                total_pages: 0,
                has_next: false,
                has_prev: false,
            });
        }
    }, [activeConversation, user?.id]);
    
    // Handle message input change
    const handleMessageInputChange = (value) => {
        setMessageInput(value);
        
        // Emit typing event when user types
        if (socketConnection && recipientsData && recipientsData.length > 0) {
            const userIds = recipientsData.map(item => item.user_id);
            
            socketConnection.emit('typingStart', {
                threadId: activeConversation,
                userIds,
                typingUser: user?.display_name || 'Someone',
                userId: user?.id
            });
        }
    };

    // Handle reacting to a message
    const handleReactToMessage = (messageId, emoji) => {
        if (!messageId || !emoji || !user?.id) return;

        const submit = async () => {
            try {
                await addReaction(messageId, emoji);
                await refreshMessageReactions(messageId);
                if (socketConnection) {
                    socketConnection.emit('sendReaction', {
                        threadId: activeConversation,
                        messageId,
                        reaction: emoji,
                        userId: user.id,
                    });
                }
            } catch (err) {
                console.error('Failed to add reaction', err);
                // Optimistic update fallback
                updateMessageReactions({
                    messageId,
                    emoji,
                    userId: user.id,
                });
            } finally {
                setReactionPicker(null);
            }
        };

        submit();
    };
    
    // Messages pagination handlers
    const handleMessagesNextPage = () => {
        if (messagesPagination.has_next && !isLoadingMore) {
            // Store current scroll position and first message ID before loading
            const scrollContainer = chatMessagesRef.current;
            const firstMessageId = messages.length > 0 ? messages[0].id : null;
            const scrollHeightBefore = scrollContainer ? scrollContainer.scrollHeight : 0;
            
            const nextPage = messagesPagination.page + 1;
            fetchMessagesData(nextPage, firstMessageId, scrollHeightBefore);
        }
    };
    
    const handleMessagesPrevPage = () => {
        if (messagesPagination.has_prev && !isLoadingMore) {
            const prevPage = messagesPagination.page - 1;
            fetchMessagesData(prevPage);
        }
    };
    
    // Header menu handlers
    const handleHeaderMenuClick = (e) => {
        e.stopPropagation();
        setShowHeaderMenu(!showHeaderMenu);
    };
    
    const handleMarkAsUnread = async () => {
        if (!activeConversation) return;
        
        try {
            await markConversationUnread(activeConversation);
            setShowHeaderMenu(false);
            // Optionally refresh conversations list in parent component
            if (onMessageSent) {
                onMessageSent({ type: 'unread', threadId: activeConversation });
            }
        } catch (error) {
            console.error('Failed to mark as unread:', error);
            setError('Failed to mark conversation as unread');
        }
    };
    
    const handleViewMembers = () => {
        setShowMembersModal(true);
        setShowHeaderMenu(false);
    };
    
    const handleDeleteMessagesMode = () => {
        setDeleteMode(true);
        setSelectedMessages(new Set());
        setShowHeaderMenu(false);
    };
    
    const handleCancelDeleteMode = () => {
        setDeleteMode(false);
        setSelectedMessages(new Set());
    };
    
    const handleToggleMessageSelection = (messageId) => {
        if (!deleteMode) return;
        
        setSelectedMessages(prev => {
            const newSet = new Set(prev);
            if (newSet.has(messageId)) {
                newSet.delete(messageId);
            } else {
                newSet.add(messageId);
            }
            return newSet;
        });
    };
    
    const handleBulkDeleteMessages = async () => {
        if (selectedMessages.size === 0) return;
        
        if (!window.confirm(`Are you sure you want to delete ${selectedMessages.size} message(s)?`)) {
            return;
        }
        
        try {
            // Delete messages one by one (same as React Native)
            const messageIdsArray = Array.from(selectedMessages);
            for (const messageId of messageIdsArray) {
                await deleteMessage(messageId);
            }
            
            // Update local state optimistically
            setMessages(prev => prev.map(m => 
                selectedMessages.has(m.id) ? { ...m, is_deleted: true } : m
            ));
            
            // Emit socket event for real-time updates (same format as React Native)
            if (socketConnection && recipientsData.length > 0) {
                const recipientIds = recipientsData.map(recipient => recipient.user_id);
                recipientIds.push(user?.id);
                
                const dataMessages = {
                    recipientIds,
                    messageIds: messageIdsArray  // Use messageIds (plural) array, not messageId
                };
                
                // Use 'deletemessage' (lowercase) event name like React Native
                socketConnection.emit('deletemessage', dataMessages);
            }
            
            // Reset delete mode
            setDeleteMode(false);
            setSelectedMessages(new Set());
        } catch (error) {
            console.error('Failed to delete messages:', error);
            setError('Failed to delete some messages');
        }
    };
    
    const handleDeleteMessage = async (messageId) => {
        if (!messageId) return;
        
        if (!window.confirm('Are you sure you want to delete this message?')) {
            return;
        }
        
        try {
            await deleteMessage(messageId);
            setMessageContextMenu(null);
            
            // Update local state optimistically
            setMessages(prev => prev.map(m => 
                m.id === messageId ? { ...m, is_deleted: true } : m
            ));
            
            // Emit socket event for real-time updates (same format as React Native)
            if (socketConnection && recipientsData.length > 0) {
                const recipientIds = recipientsData.map(recipient => recipient.user_id);
                recipientIds.push(user?.id);
                
                const dataMessages = {
                    recipientIds,
                    messageIds: [messageId]  // Use messageIds (plural) array
                };
                
                // Use 'deletemessage' (lowercase) event name like React Native
                socketConnection.emit('deletemessage', dataMessages);
            }
        } catch (error) {
            console.error('Failed to delete message:', error);
            setError('Failed to delete message');
        }
    };
    
    const handleMessageContextMenu = (e, messageId) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Show context menu for all non-deleted messages
        const message = messages.find(m => m.id === messageId);
        if (message && !message.is_deleted) {
            // Get coordinates from touch or mouse event (for mobile support)
            const clientX = e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY;
            
            // Use fixed positioning coordinates (clientX, clientY) since menu is outside message wrapper
            setMessageContextMenu({
                messageId,
                x: clientX || 0,
                y: clientY || 0
            });
        }
    };

    // Long press handler for mobile
    const handleTouchStart = (e, messageId) => {
        if (deleteMode) return;
        
        const message = messages.find(m => m.id === messageId);
        if (!message || message.is_deleted) return;
        
        const touch = e.touches[0];
        touchStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            messageId
        };
        
        longPressTimerRef.current = setTimeout(() => {
            if (touchStartRef.current && touchStartRef.current.messageId === messageId) {
                // Trigger context menu on long press
                const syntheticEvent = {
                    preventDefault: () => {},
                    stopPropagation: () => {},
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                    touches: e.touches
                };
                handleMessageContextMenu(syntheticEvent, messageId);
            }
        }, 500); // 500ms long press
    };

    const handleTouchEnd = (e) => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        touchStartRef.current = null;
    };

    const handleTouchMove = (e) => {
        // Cancel long press if user moves finger
        if (touchStartRef.current && longPressTimerRef.current) {
            const touch = e.touches[0];
            const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
            const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
            
            // If moved more than 10px, cancel long press
            if (deltaX > 10 || deltaY > 10) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = null;
                touchStartRef.current = null;
            }
        }
    };

    const handleStartEditMessage = (messageId) => {
        const target = messages.find((m) => m.id === messageId);
        if (!target || !isMyMessage(target) || target.is_deleted) return;

        const plainText = getPlainMessageText(target.message || target.content || '');
        setEditingMessage({ id: target.id });
        setMessageInput(plainText);
        setMessageContextMenu(null);
        setReplyingToMessage(null); // Clear reply when editing
    };
    
    const handleReplyToMessage = (messageId) => {
        const target = messages.find((m) => m.id === messageId);
        if (!target || target.is_deleted) return;
        
        setReplyingToMessage(target);
        setEditingMessage(null); // Clear edit when replying
        setMessageContextMenu(null);
        // Focus on input
        setTimeout(() => {
            const textarea = document.querySelector('.octopus-chat-input');
            if (textarea) {
                textarea.focus();
            }
        }, 100);
    };
    
    const handleCancelReply = () => {
        setReplyingToMessage(null);
    };
    
    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
                setShowHeaderMenu(false);
            }
            if (messageMenuRef.current && !messageMenuRef.current.contains(event.target)) {
                setMessageContextMenu(null);
            }
            if (reactionPickerRef.current && !reactionPickerRef.current.contains(event.target)) {
                setReactionPicker(null);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            // Cleanup long press timer
            if (longPressTimerRef.current) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = null;
            }
        };
    }, []);
    
    // Fetch contacts (matches React Native: fetchContacts)
    const fetchContacts = async (query = '') => {
        try {
            setIsSearchingRecipients(true);
            setShowContacts(true);
            
            const response = await searchUsers({ search: query, page: 1, size: 20 });
            
            // Match React Native response structure: Response.data.users.users and Response.data.users.pagination
            if (response && response.data && response.data.users && response.data.users.users && response.data.users.users.length > 0) {
                const users = response.data.users.users;
                // Filter out current user and already selected recipients
                // Handle both ID (uppercase) and id (lowercase) fields
                const filteredUsers = users.filter(u => {
                    const userId = u.ID || u.id;
                    const currentUserId = user?.id || user?.ID;
                    return userId !== currentUserId && !selectedRecipients.find(r => (r.ID || r.id) === userId);
                });
                setContacts(filteredUsers);
                setRecipientSearchResults(filteredUsers);
            } else {
                setContacts([]);
                setRecipientSearchResults([]);
            }
        } catch (error) {
            console.error('Failed to fetch contacts:', error);
            setContacts([]);
            setRecipientSearchResults([]);
        } finally {
            setIsSearchingRecipients(false);
        }
    };
    
    // Fetch contacts on mount (matches React Native)
    useEffect(() => {
        if (isNewMessageMode) {
            fetchContacts();
        }
    }, [isNewMessageMode]);
    
    // Debounced search query (matches React Native: 1000ms delay)
    useEffect(() => {
        if (!isNewMessageMode) return;
        
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(recipientSearchQuery);
        }, 1000);
        
        return () => clearTimeout(timer);
    }, [recipientSearchQuery, isNewMessageMode]);
    
    // Fetch contacts when debounced search query changes
    useEffect(() => {
        if (!isNewMessageMode) return;
        
        if (debouncedSearchQuery !== '') {
            fetchContacts(debouncedSearchQuery);
        } else {
            fetchContacts(); // Fetch all contacts when search is cleared
        }
    }, [debouncedSearchQuery, isNewMessageMode]);
    
    // Add recipient (matches React Native: handleSelectUser)
    const handleAddRecipient = (user) => {
        // Handle both ID (uppercase) and id (lowercase) fields
        const userId = user.ID || user.id;
        if (!selectedRecipients.find(r => (r.ID || r.id) === userId)) {
            setSelectedRecipients(prev => [...prev, user]);
            setShowContacts(false);
        }
        setRecipientSearchQuery('');
    };
    
    // Remove recipient (matches React Native: handleRemoveUser)
    const handleRemoveRecipient = (userId) => {
        setSelectedRecipients(prev => prev.filter(r => (r.ID || r.id) !== userId));
    };
    
    // Handle new message send (matches React Native: handleSendMessage)
    const handleSendNewMessage = async (payload = {}) => {
        if (selectedRecipients.length === 0) {
            setError('Please select at least one recipient');
            return false;
        }
        
        const textFromPayload =
            typeof payload.message === 'string'
                ? payload.message
                : typeof payload.text === 'string'
                ? payload.text
                : messageInput;
        const messageToSend = (textFromPayload || '').trim();

        const attachmentsArray = Array.isArray(payload.attachments)
            ? payload.attachments
            : [];

        const images =
            Array.isArray(payload.images) && payload.images.length > 0
                ? payload.images
                : attachmentsArray.filter(({ kind }) => kind === 'image');

        const videos =
            Array.isArray(payload.videos) && payload.videos.length > 0
                ? payload.videos
                : attachmentsArray.filter(({ kind }) => kind === 'video');

        const documents =
            Array.isArray(payload.documents) && payload.documents.length > 0
                ? payload.documents
                : attachmentsArray.filter(
                      ({ kind }) => kind === 'document' || (kind !== 'image' && kind !== 'video')
                  );

        if (!messageToSend && images.length === 0 && videos.length === 0 && documents.length === 0) {
            setError('Please enter a message or attach a file');
            return false;
        }
        
        setSendingMessage(true);
        setError(null);
        
        try {
            // Use 'users' field and handle both ID (uppercase) and id (lowercase) fields
            const userIds = selectedRecipients.map(r => r.ID || r.id);
            
            const response = await createNewMessage({
                users: userIds, // Use 'users' not 'recipients' to match React Native
                message: messageToSend,
                images,
                videos,
                documents
            });
            
            // Match React Native response structure: Response.data.thread_id and Response.data.message_id
            if (response && response.isSuccess && response.data && response.data.thread_id && response.data.message_id) {
                // Emit socket events (matches React Native)
                if (socketConnection && selectedRecipients && selectedRecipients.length > 0) {
                    const recipientIds = selectedRecipients.map(r => r.ID || r.id);
                    const fcmTokenUsers = [...recipientIds];
                    recipientIds.push(user?.id || user?.ID);
                    
                    const dataMessages = {
                        recipientIds,
                        messageId: response.data.message_id
                    };
                    
                    socketConnection.emit('sendmessage', dataMessages);
                    socketConnection.emit('fcmTokens', {
                        fcmTokenUsers,
                        messageId: response.data.message_id
                    });
                }
                
                setMessageInput('');
                setSelectedRecipients([]);
                setRecipientSearchQuery('');
                
                if (onNewMessageSent) {
                    onNewMessageSent(response.data.thread_id);
                }
                return true;
            } else {
                throw new Error(response?.message || 'Failed to get thread ID from response');
            }
        } catch (err) {
            console.error('Failed to send new message:', err);
            setError(err.message || 'Failed to send message');
            return false;
        } finally {
            setSendingMessage(false);
        }
    };
    
    // Reset new message mode when it changes
    useEffect(() => {
        if (!isNewMessageMode) {
            setSelectedRecipients([]);
            setRecipientSearchQuery('');
            setDebouncedSearchQuery('');
            setContacts([]);
            setRecipientSearchResults([]);
            setShowRecipientSearch(false);
            setShowContacts(true);
            setMessageInput('');
        }
    }, [isNewMessageMode]);
    
    // Close recipient search when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (recipientSearchRef.current && !recipientSearchRef.current.contains(event.target) &&
                recipientInputRef.current && !recipientInputRef.current.contains(event.target)) {
                setShowRecipientSearch(false);
            }
        };
        
        if (isNewMessageMode) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isNewMessageMode]);
    
    // Show new message composer
    if (isNewMessageMode) {
        return (
            <div className="octopus-messages-chat">
                {/* New Message Header - title bar + close */}
                <div className="octopus-chat-header octopus-new-message-header-bar">
                    <div className="octopus-new-message-header-left">
                        {onBack && (
                            <button
                                className="octopus-mobile-back-btn"
                                onClick={onBack}
                                title="Back to conversations"
                                aria-label="Back to conversations"
                            >
                                <i className="bb-icon-f bb-icon-arrow-left"></i>
                            </button>
                        )}
                        <h2 className="octopus-new-message-title">New message</h2>
                    </div>
                    <button
                        className="octopus-icon octopus-new-message-close"
                        onClick={onNewMessageCancel}
                        title="Close"
                        aria-label="Close"
                    >
                        <i className="bb-icon-f bb-icon-times"></i>
                    </button>
                </div>
                {/* To: + recipient selector row */}
                <div className="octopus-new-message-to-row">
                    {/* <span className="octopus-new-message-label">To:</span> */}
                    <div className="octopus-recipient-selector" ref={recipientSearchRef}>
                        <div className="octopus-recipient-chips">
                            {selectedRecipients.map(recipient => {
                                const recipientId = recipient.ID || recipient.id;
                                return (
                                    <div key={recipientId} className="octopus-recipient-chip">
                                        <span className="octopus-recipient-name">{recipient.display_name || recipient.username}</span>
                                        <button
                                            type="button"
                                            className="octopus-recipient-remove"
                                            onClick={() => handleRemoveRecipient(recipientId)}
                                            aria-label="Remove recipient"
                                        >
                                            ×
                                        </button>
                                    </div>
                                );
                            })}
                            <input
                                ref={recipientInputRef}
                                type="text"
                                className="octopus-recipient-input"
                                placeholder={selectedRecipients.length === 0 ? "Type a name or multiple names" : ""}
                                value={recipientSearchQuery}
                                onChange={(e) => {
                                    setRecipientSearchQuery(e.target.value);
                                    setShowRecipientSearch(true);
                                }}
                                onFocus={() => setShowRecipientSearch(true)}
                            />
                        </div>
                    </div>
                </div>
                
                {/* Contacts List (matches React Native) */}
                {showContacts && contacts.length > 0 && (
                    <div className="octopus-contacts-list">
                        {contacts.map(user => {
                            const userId = user.ID || user.id;
                            const isSelected = selectedRecipients.find(r => (r.ID || r.id) === userId);
                            return (
                                <div
                                    key={userId}
                                    className={`octopus-contact-item ${isSelected ? 'octopus-contact-selected' : ''}`}
                                    onClick={() => handleAddRecipient(user)}
                                >
                                    <img
                                        src={user.image || user.avatar || '/wp-content/plugins/octopus-react/assets/dummy-user.png'}
                                        alt={user.display_name || user.username}
                                        className="octopus-contact-avatar"
                                    />
                                    <div className="octopus-contact-info">
                                        <span className="octopus-contact-name">{user.display_name || user.username}</span>
                                        {user.position?.displayString && (
                                            <span className="octopus-contact-position">{user.position?.displayString}</span>
                                        )}
                                        {user.message && (
                                            <span className="octopus-contact-message">{user.message}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Empty message area - only show when no contacts list */}
                {!showContacts && (
                    <div className="octopus-chat-messages" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                        <div className="octopus-no-messages">
                            <div className="octopus-no-messages-icon">💬</div>
                            <h3>New Message</h3>
                            <p>Select recipients and start typing your message below</p>
                        </div>
                    </div>
                )}
                
                {/* Error display */}
                {error && (
                    <div className="octopus-inline-error">
                        <p>{error}</p>
                    </div>
                )}
                
                {/* Chat Input - only show when users are selected (matches React Native) */}
                {selectedRecipients.length > 0 && (
                    <ChatMessageInput
                        value={messageInput}
                        onChange={handleMessageInputChange}
                        onSend={handleSendNewMessage}
                        isSending={sendingMessage}
                        isEditing={false}
                    />
                )}
            </div>
        );
    }
    
    // Show empty state when no conversation is selected
    if (!activeConversation && !isPageLoading) {
        return (
            <div className="octopus-messages-chat">
                <div className="octopus-no-conversation-selected">
                    <div className="octopus-no-conversation-icon">💬</div>
                    <h3>Select a conversation</h3>
                    <p>Choose a conversation from the list to start messaging</p>
                </div>
            </div>
        );
    }
    if (isPageLoading) {
        return (
            <div className="octopus-messages-chat">
                <div className="octopus-loading">
                    <div className="octopus-spinner"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    return (
            <div className="octopus-messages-chat">
                {/* Chat Header */}
                <div className="octopus-chat-header">
                    {onBack && (
                        <button 
                            className="octopus-mobile-back-btn"
                            onClick={onBack}
                            title="Back to conversations"
                            aria-label="Back to conversations"
                        >
                            <i className="bb-icon-f bb-icon-arrow-left"></i>
                        </button>
                    )}
                    <div className="octopus-chat-header-left">
                    {threadInfo?.image ? (
                        <img 
                            src={threadInfo.image} 
                            alt={threadInfo.name || activeConv.name}
                            className="octopus-chat-avatar-img"
                        />
                    ) : activeConv?.avatar ? (
                        typeof activeConv.avatar === 'string' && activeConv.avatar.startsWith('http') ? (
                            <img
                                src={activeConv.avatar}
                                alt={threadInfo?.name || activeConv?.name || 'Avatar'}
                                className="octopus-chat-avatar-img"
                            />
                        ) : (
                            <span className="octopus-chat-avatar">{activeConv.avatar}</span>
                        )
                    ) : (recipientsData.length === 1 && recipientsData[0]?.userData?.image) ? (
                        <img
                            src={recipientsData[0].userData.image}
                            alt={recipientsData[0].userData.display_name || recipientsData[0].userData.name || 'Avatar'}
                            className="octopus-chat-avatar-img"
                        />
                    ) : (
                        <span className="octopus-chat-avatar">{'💬'}</span>
                    )}
                    <div className="octopus-chat-header-info">
                        <span className="octopus-chat-name">
                            {(() => {
                                // First try thread/group name
                                if (threadInfo?.name) return threadInfo.name;
                                if (activeConv?.name) return activeConv.name;
                                
                                // If no thread/group name, show all recipient names
                                if (recipientsData && recipientsData.length > 0) {
                                    const recipientNames = recipientsData
                                        .map(recipient => recipient.userData?.display_name || recipient.userData?.name)
                                        .filter(name => name && name.trim() !== '');
                                    return recipientNames.length > 0 ? recipientNames.join(', ') : 'Conversation';
                                }
                                
                                return 'Conversation';
                            })()}
                        </span>

                        {(() => {
                            if (recipientsData && recipientsData.length === 1 && recipientsData[0].userData?.position?.displayString) {
                                return (
                                    <span className="octopus-chat-description">
                                        {recipientsData[0].userData?.position?.displayString}
                                    </span>
                                );
                            }
                            
                            return null;
                        })()}
                        
                        {threadInfo?.description && (
                            <span className="octopus-chat-description" title={threadInfo.description}>
                                {threadInfo.description.substring(0, 60)}
                                {threadInfo.description.length > 60 ? '...' : ''}
                            </span>
                        )}
                    </div>
                </div>
                <div className="octopus-chat-header-menu" ref={headerMenuRef}>
                    {onBack && (
                        <button
                            className="octopus-chat-close-btn"
                            onClick={onBack}
                            title="Close chat"
                            aria-label="Close chat"
                        >
                            <i className="bb-icon-f bb-icon-times"></i>
                        </button>
                    )}
                    <span className="octopus-icon" onClick={handleHeaderMenuClick}>
                        <i className="bb-icon-f bb-icon-ellipsis-h"></i>
                    </span>
                    {showHeaderMenu && (
                        <div className="octopus-context-menu">
                            {/* <button className="octopus-menu-item" onClick={handleMarkAsUnread}>
                                <span className="octopus-menu-icon">
                                    <i className="bb-icon-f bb-icon-link"></i>
                                </span>
                                <span>Mark as unread</span>
                            </button> */}
                            <button className="octopus-menu-item" onClick={handleViewMembers}>
                                <span className="octopus-menu-icon">
                                    <i className="bb-icon-f bb-icon-users"></i>
                                </span>
                                <span>View members</span>
                            </button>
                            <button className="octopus-menu-item" onClick={handleDeleteMessagesMode}>
                                <span className="octopus-menu-icon">
                                    <i className="bb-icon-f bb-icon-trash"></i>
                                </span>
                                <span>Delete messages</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Mode Header */}
            {deleteMode && (
                <div className="octopus-delete-mode-header">
                    <div className="octopus-delete-mode-info">
                        <span>
                            {selectedMessages.size === 0 
                                ? 'Select messages to delete' 
                                : `${selectedMessages.size} message${selectedMessages.size > 1 ? 's' : ''} selected`}
                        </span>
                    </div>
                    <div className="octopus-delete-mode-actions">
                        <button 
                            className="octopus-btn-secondary" 
                            onClick={handleCancelDeleteMode}
                        >
                            ✕ Cancel
                        </button>
                        <button 
                            className="octopus-btn-danger" 
                            onClick={handleBulkDeleteMessages}
                            disabled={selectedMessages.size === 0}
                        >
                            🗑️ Delete {selectedMessages.size > 0 && `(${selectedMessages.size})`}
                        </button>
                    </div>
                </div>
            )}
            
            {/* Messages Area */}
            <div className="octopus-chat-messages" ref={chatMessagesRef}>
                {isLoading && messages.length === 0 ? (
                    <div className="octopus-loading">
                        <div className="octopus-spinner"></div>
                        <p>Loading messages...</p>
                    </div>
                ) : error && messages.length === 0 ? (
                    <div className="octopus-error-messages">
                        <p>Error: {error}</p>
                        <button onClick={() => fetchMessagesData(1)} className="octopus-retry-btn">
                            Retry
                        </button>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="octopus-no-messages">
                        <div className="octopus-no-messages-icon">💬</div>
                        <h3>No Messages Yet</h3>
                        <p>Be the first to send a message in this conversation</p>
                    </div>
                ) : (
                    <>
                        {/* Load Older Messages at Top (when scrolling up) */}
                        {messagesPagination.has_next && (
                            <div className="octopus-load-more-top">
                                <button 
                                    onClick={handleMessagesNextPage}
                                    disabled={isLoadingMore}
                                    className="octopus-load-more-btn"
                                >
                                    {isLoadingMore ? 'Loading...' : '↑ Load Older Messages'}
                                </button>
                            </div>
                        )}
                        
                        {messages.map((message, index) => {
                            const isMine = isMyMessage(message);
                            const showDate =
                                index === 0 ||
                                (messages[index - 1] && 
                                 new Date(message.date || message.date_sent).toDateString() !== 
                                 new Date(messages[index - 1].date || messages[index - 1].date_sent).toDateString());
                            
                            const sanitizedContent = sanitizeMessageHtml(message.message || message.content);
                            const attachments = Array.isArray(message.attachments)
                                ? message.attachments.filter((attachment) => getAttachmentPreviewUrl(attachment))
                                : [];
                            const inlineImageUrl =
                                message.type === 'image' && attachments.length === 0
                                    ? message.imageUrl || message.image_url
                                    : null;
                            const messageReactions = Array.isArray(message.reactions) ? message.reactions : [];

                            return (
                                    <div
                                        key={message.id}
                                        data-message-id={message.id}
                                        className={`octopus-message-wrapper ${deleteMode ? 'octopus-delete-mode' : ''}`}
                                    >
                                    {/* Date Separator */}
                                    {showDate && (
                                        <div className="octopus-date-separator">
                                            <span className="octopus-date-separator-text">
                                                {formatMessageDate(message.date || message.date_sent)}
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Message Bubble */}
                                    <div
                                        className={`octopus-message-item ${isMine ? 'octopus-message-mine' : 'octopus-message-theirs'} ${deleteMode && selectedMessages.has(message.id) ? 'octopus-message-selected' : ''}`}
                                    >
                                        {/* Delete Mode Checkbox */}
                                        {deleteMode && isMyMessage(message) && !message.is_deleted && (
                                            <div className="octopus-message-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedMessages.has(message.id)}
                                                    onChange={() => handleToggleMessageSelection(message.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        )}
                                        {/* Avatar for other users */}
                                        {/* {!isMine && ( */}
                                            <img 
                                                src={message.userData?.image || message.author?.avatar || '/wp-content/plugins/octopus-react/assets/dummy-user.png'} 
                                                alt={message.userData?.display_name || message.author?.name || 'User'}
                                                className="octopus-message-avatar"
                                            />
                                        {/* )} */}
                                        
                                        <div
                                            className={`octopus-message-bubble ${isMine ? 'octopus-bubble-mine' : 'octopus-bubble-theirs'}`}
                                            onContextMenu={(e) => !deleteMode && handleMessageContextMenu(e, message.id)}
                                            onTouchStart={(e) => !deleteMode && handleTouchStart(e, message.id)}
                                            onTouchEnd={handleTouchEnd}
                                            onTouchMove={handleTouchMove}
                                            onClick={(e) => {
                                                // Reserved for future click behavior on the message bubble
                                            }}
                                        >
                                            {/* Sender name for group chats */}
                                            {/* {isMine && (
                                                <div className="octopus-sender-name">
                                                    {message.userData?.display_name || message.author?.name || 'Unknown User'}
                                                </div>
                                            )} */}
                                            <div className="octopus-sender-name">
                                                <span>{message.userData?.display_name || message.author?.name || 'Unknown User'}</span>
                                                {/* Timestamp */}
                                                <div className="octopus-message-time">
                                                    {formatMessageTime(message.date || message.date_sent)}
                                                </div>
                                            </div>
                                            
                                            {/* Reply Preview - Show quoted message if this is a reply */}
                                            {message.reply_to && message.reply_to_message && !message.is_deleted && (
                                                <div
                                                    className="octopus-message-reply-preview"
                                                    onClick={() => ensureMessageVisibleAndScroll(message.reply_to)}
                                                >
                                                    <div className="octopus-reply-preview-line"></div>
                                                    <div className="octopus-reply-preview-content">
                                                        <div className="octopus-reply-preview-sender">
                                                            {message.reply_to_message.userData?.display_name || message.reply_to_message.author?.name || 'Unknown User'}
                                                        </div>
                                                        <div className="octopus-reply-preview-text">
                                                            {getPlainMessageText(message.reply_to_message.message || message.reply_to_message.content || '').substring(0, 100)}
                                                            {getPlainMessageText(message.reply_to_message.message || message.reply_to_message.content || '').length > 100 ? '...' : ''}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {(message.is_deleted) ? (
                                                <div className="octopus-message-content deleted-message">
                                                    This message was deleted
                                                </div>
                                            ) : (message.messageMeta && message.messageMeta.group_message_group_joined && message.messageMeta.group_message_group_joined == "yes")? (
                                            <> 
                                                <div className="octopus-message-content group-joined-message">
                                                     Joined Group
                                                </div>
                                            </>
                                            ) : (
                                                <>
                                                
                                                
                                                {/* Message Content */}
                                                {sanitizedContent && sanitizedContent.trim() !== '' && (
                                                    <div
                                                        className="octopus-message-content"
                                                        dangerouslySetInnerHTML={{
                                                            __html: sanitizedContent,
                                                        }}
                                                    />
                                                )}

                                                {/* Inline image for legacy messages */}
                                                {inlineImageUrl && (
                                                    <div className="octopus-message-image">
                                                        <img src={inlineImageUrl} alt={message.filename || 'Image attachment'} />
                                                        {message.filename && (
                                                            <div className="octopus-image-filename">{message.filename}</div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Attachments */}
                                                {attachments.length > 0 && (
                                                    <div className="octopus-message-attachments">
                                                        {attachments.map((attachment, attachmentIndex) => {
                                                            const previewUrl = getAttachmentPreviewUrl(attachment);
                                                            const displayName = getAttachmentDisplayName(attachment);
                                                            const downloadUrl = getAttachmentDownloadUrl(attachment) || previewUrl;
                                                            const key =
                                                                attachment.ID ||
                                                                attachment.attachment_id ||
                                                                attachment.media?.id ||
                                                                attachmentIndex;
                                                            const kind = getAttachmentKind(attachment);

                                                            if (kind === 'photo') {
                                                                return (
                                                                    <div
                                                                        key={key}
                                                                        className="octopus-message-attachment octopus-attachment-card octopus-attachment-card-photo"
                                                                    >
                                                                        {previewUrl ? (
                                                                            <div className="octopus-attachment-photo-inner">
                                                                                <img
                                                                                    src={previewUrl}
                                                                                    alt={displayName}
                                                                                    className="octopus-attachment-photo-image"
                                                                                    onClick={() => handleAttachmentPreview(attachment, previewUrl)}
                                                                                />
                                                                                <div className="octopus-attachment-overlay">
                                                                                    {/* <button
                                                                                        type="button"
                                                                                        className="octopus-attachment-icon-button"
                                                                                        onClick={() => handleAttachmentPreview(attachment, previewUrl)}
                                                                                    >
                                                                                        <span className="octopus-attachment-icon" aria-hidden="true">👁️</span>
                                                                                        <span className="octopus-sr-only">Preview image</span>
                                                                                    </button> */}
                                                                                    {downloadUrl && (
                                                                                        <a
                                                                                            href={downloadUrl}
                                                                                            download
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="octopus-attachment-icon-button octopus-attachment-icon-button-download"
                                                                                        >
                                                                                            <span className="octopus-attachment-icon" aria-hidden="true">⬇️</span>
                                                                                            <span className="octopus-sr-only">Download image</span>
                                                                                        </a>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="octopus-attachment-fallback">{displayName}</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }

                                                            if (kind === 'video') {
                                                                return (
                                                                    <div
                                                                        key={key}
                                                                        className="octopus-message-attachment octopus-attachment-card octopus-attachment-card-video"
                                                                    >
                                                                        {previewUrl ? (
                                                                            <>
                                                                                <video
                                                                                    controls
                                                                                    src={previewUrl}
                                                                                    className="octopus-attachment-video-player"
                                                                                >
                                                                                    {displayName}
                                                                                </video>
                                                                                {downloadUrl && (
                                                                                    <a
                                                                                        href={downloadUrl}
                                                                                        download
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="octopus-attachment-action octopus-attachment-download-link"
                                                                                    >
                                                                                        <span className="octopus-attachment-icon" aria-hidden="true">⬇️</span>
                                                                                        <span>Download</span>
                                                                                    </a>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <span className="octopus-attachment-fallback">{displayName}</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }

                                                            return (
                                                                <div
                                                                    key={key}
                                                                    className="octopus-message-attachment octopus-attachment-card octopus-attachment-card-file"
                                                                >
                                                                    <span className="octopus-attachment-file-icon" aria-hidden="true">📄</span>
                                                                    <span className="octopus-attachment-file-name">{displayName}</span>
                                                                    {downloadUrl ? (
                                                                        <a
                                                                            href={downloadUrl}
                                                                            download
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="octopus-attachment-action octopus-attachment-download-link"
                                                                        >
                                                                            <span className="octopus-attachment-icon" aria-hidden="true">⬇️</span>
                                                                            <span>Download</span>
                                                                        </a>
                                                                    ) : (
                                                                        <span className="octopus-attachment-unavailable">Unavailable</span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                
                                                
                                                </>
                                            )}
                                            {/* Reactions */}
                                            {!message.is_deleted && (
                                                <div className="octopus-message-reaction-row">
                                                    {messageReactions.length > 0 && (
                                                        <>
                                                        <div className="octopus-message-reactions">
                                                            {messageReactions.map((reaction) => {
                                                                const users = Array.isArray(reaction.users)
                                                                    ? reaction.users
                                                                    : [];
                                                                const reactedByMe = users.some(
                                                                    (id) => String(id) === String(user?.id)
                                                                );
                                                                const count = users.length || 1;
                                                                return (
                                                                    <button
                                                                        key={`${message.id}-${reaction.emoji}`}
                                                                        type="button"
                                                                        className={`octopus-reaction-chip ${reactedByMe ? 'octopus-reaction-chip-active' : ''}`}
                                                                        onClick={() =>
                                                                            handleReactToMessage(message.id, reaction.emoji)
                                                                        }
                                                                    >
                                                                        <span className="octopus-reaction-emoji">
                                                                            {reaction.emoji}
                                                                        </span>
                                                                        <span className="octopus-reaction-count">
                                                                            {count}
                                                                        </span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                        </>
                                                    )}
                                                    {/* <div className="octopus-reaction-actions">
                                                        <button
                                                            type="button"
                                                            className="octopus-reaction-trigger octopus-icon-button bb-icon-smile"
                                                            onClick={() =>
                                                                setReactionPicker({ messageId: message.id })
                                                            }
                                                            aria-label="Add reaction"
                                                        >
                                                        </button>
                                                    </div> */}
                                                    {reactionPicker?.messageId === message.id && (
                                                        <div
                                                            className="octopus-reaction-picker"
                                                            ref={reactionPickerRef}
                                                        >
                                                            <div className="octopus-quick-reactions">
                                                                {quickReactions.map((emoji) => (
                                                                    <button
                                                                        key={emoji}
                                                                        type="button"
                                                                        onClick={() =>
                                                                            handleReactToMessage(message.id, emoji)
                                                                        }
                                                                        className="octopus-quick-reaction-btn"
                                                                    >
                                                                        {emoji}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        
                        {/* Message Context Menu - Outside messages */}
                        {messageContextMenu && (() => {
                            const message = messages.find(m => m.id === messageContextMenu.messageId);
                            if (!message || message.is_deleted) return null;
                            
                            // Calculate position with viewport boundary checks for mobile
                            const menuWidth = 200; // Approximate menu width
                            const menuHeight = 150; // Approximate menu height
                            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                            
                            let x = messageContextMenu.x || 0;
                            let y = messageContextMenu.y || 0;
                            
                            // Adjust if menu would go off-screen horizontally
                            if (x + menuWidth > viewportWidth) {
                                x = viewportWidth - menuWidth - 10;
                            }
                            if (x < 10) {
                                x = 10;
                            }
                            
                            // Adjust if menu would go off-screen vertically
                            if (y + menuHeight > viewportHeight) {
                                y = viewportHeight - menuHeight - 10;
                            }
                            if (y < 10) {
                                y = 10;
                            }
                            
                            return (
                                <div 
                                    ref={messageMenuRef}
                                    className="octopus-message-context-menu"
                                    style={{
                                        position: 'fixed',
                                        left: `${x}px`,
                                        top: `${y}px`,
                                        zIndex: 1000
                                    }}
                                >
                                    <button 
                                        className="octopus-menu-item" 
                                        onClick={() => {
                                            handleReplyToMessage(messageContextMenu.messageId);
                                            setMessageContextMenu(null);
                                        }}
                                    >
                                        <span className="octopus-menu-icon">
                                            <i className="bb-icon-f bb-icon-reply"></i>
                                        </span>
                                        <span>Reply</span>
                                    </button>
                                    {isMyMessage(message) && (
                                        <>
                                            <button 
                                                className="octopus-menu-item" 
                                                onClick={() => handleStartEditMessage(messageContextMenu.messageId)}
                                            >
                                                <span className="octopus-menu-icon">
                                                    <i className="bb-icon-f bb-icon-edit"></i>
                                                </span>
                                                <span>Edit message</span>
                                            </button>
                                            <button 
                                                className="octopus-menu-item" 
                                                onClick={() => handleDeleteMessage(messageContextMenu.messageId)}
                                            >
                                                <span className="octopus-menu-icon">
                                                    <i className="bb-icon-f bb-icon-trash"></i>
                                                </span>
                                                <span>Delete message</span>
                                            </button>
                                        </>
                                    )}
                                </div>
                            );
                        })()}
                        
                        {/* Scroll anchor */}
                        <div ref={messagesEndRef} />
                        
                        
                        
                        {/* Load Newer Messages at Bottom (only if viewing older pages) */}
                        {/* {messagesPagination.has_prev && (
                            <div className="octopus-load-more-bottom">
                                <button 
                                    onClick={handleMessagesPrevPage}
                                    disabled={isLoadingMore}
                                    className="octopus-load-more-btn"
                                >
                                    {isLoadingMore ? 'Loading...' : '↓ Load Newer Messages'}
                                </button>
                            </div>
                        )} */}
                        
                        {/* Error display if error occurs during pagination */}
                        {error && messages.length > 0 && (
                            <div className="octopus-inline-error">
                                <p>{error}</p>
                                <button onClick={() => fetchMessagesData(1)} className="octopus-retry-btn-small">
                                    Retry
                                </button>
                            </div>
                        )}
                        
                        {/* Pagination Info */}
                        {/* {messagesPagination.total_pages > 1 && (
                            <div className="octopus-pagination-info">
                                Page {messagesPagination.page} of {messagesPagination.total_pages}
                                {messagesPagination.total > 0 && ` (${messagesPagination.total} total messages)`}
                            </div>
                        )} */}
                    </>
                )}
            </div>
            {/* Typing Indicator */}
            {typingUsers.size > 0 && (
                <div className="octopus-typing-indicator">
                    <div className="octopus-typing-text">
                        {Array.from(typingUsers.values()).length === 1 
                            ? `${Array.from(typingUsers.values())[0]} is typing...` 
                            : `${Array.from(typingUsers.values()).join(', ')} are typing...`}
                    </div>
                </div>
            )}

            {/* Reply Preview Banner */}
            {replyingToMessage && (
                <div className="octopus-reply-preview-banner">
                    <div className="octopus-reply-preview-banner-content">
                        <div className="octopus-reply-preview-banner-left">
                            <div className="octopus-reply-preview-line"></div>
                            <div className="octopus-reply-preview-info">
                                <div className="octopus-reply-preview-label">
                                    Replying to {replyingToMessage.userData?.display_name || replyingToMessage.author?.name || 'Unknown User'}
                                </div>
                                <div className="octopus-reply-preview-text">
                                    {getPlainMessageText(replyingToMessage.message || replyingToMessage.content || '').substring(0, 80)}
                                    {getPlainMessageText(replyingToMessage.message || replyingToMessage.content || '').length > 80 ? '...' : ''}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="octopus-reply-preview-cancel"
                            onClick={handleCancelReply}
                            aria-label="Cancel reply"
                        >
                            <i className="bb-icon-f bb-icon-times"></i>
                        </button>
                    </div>
                </div>
            )}

            {/* Chat Input */}
            <ChatMessageInput
                value={messageInput}
                onChange={handleMessageInputChange}
                onSend={editingMessage ? handleEditExistingMessage : handleSendMessage}
                isSending={sendingMessage}
                isEditing={!!editingMessage}
                onCancelEdit={() => {
                    setEditingMessage(null);
                    setMessageInput('');
                }}
            />

            {previewAttachment && (
                <div className="octopus-preview-overlay" onClick={handleClosePreview}>
                    <div className="octopus-preview-content" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="octopus-preview-close" onClick={handleClosePreview}>
                            <span aria-hidden="true">×</span>
                            <span className="octopus-sr-only">Close preview</span>
                        </button>

                        <img
                            src={previewAttachment.url}
                            alt={previewAttachment.name}
                            className="octopus-preview-image"
                        />
                        {previewAttachment.name && (
                            <div className="octopus-preview-title">{previewAttachment.name}</div>
                        )}
                    </div>
                </div>
            )}
            
            {/* View Members Modal */}
            {showMembersModal && (
                <div className="octopus-modal-overlay" onClick={() => setShowMembersModal(false)}>
                    <div className="octopus-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="octopus-modal-header">
                            <h3>Members</h3>
                            <button 
                                className="octopus-modal-close" 
                                onClick={() => setShowMembersModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="octopus-modal-body">
                            {membersData.length > 0 ? (
                                <div className="octopus-members-list">
                                    {membersData.map((recipient) => (
                                        <div key={recipient.user_id} className="octopus-member-item">
                                            <img 
                                                src={recipient.userData?.image || '/wp-content/plugins/octopus-react/assets/dummy-user.png'} 
                                                alt={recipient.userData?.display_name || 'User'}
                                                className="octopus-member-avatar"
                                            />
                                            <div className="octopus-member-info">
                                                <span className="octopus-member-name">
                                                    {recipient.userData?.display_name || 'Unknown User'} {recipient.user_id === user?.id ? '(You)' : ''}
                                                </span>
                                                {recipient.userData?.email && (
                                                    <span className="octopus-member-email">
                                                        {recipient.userData.email}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="octopus-no-members">
                                    <p>No members found</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatPanel;
